class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.detailsContainer = this.closest('details');
    this.overlayContent = this.closest('.search-modal__content--debute');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.detailsContainer?.addEventListener('toggle', this.onDetailsToggle.bind(this));
    this.predictiveSearchResults.addEventListener('scroll', this.updateScrollMaskState.bind(this), { passive: true });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.closeResults(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeResults();
    });
  }

  onDetailsToggle() {
    if (!this.detailsContainer?.hasAttribute('open')) this.closeResults(true);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.closeResults(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const termMatches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (!termMatches) return;

      if (termMatches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const predictiveSearchUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[type]=product,query&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;

    fetch(predictiveSearchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!resultsSection) {
          this.close();
          console.warn('Predictive search response did not include the predictive-search section.');
          return;
        }

        const resultsMarkup = resultsSection.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        console.warn('Predictive search request failed.', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.scrollTop = 0;
    this.setAttribute('results', true);

    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const isMobileMenu = window.matchMedia('(max-width: 1100px)').matches;
    const field = this.input.closest('.field');
    const resultsStyles = window.getComputedStyle(this.predictiveSearchResults);
    const resultsTopSpace = parseFloat(resultsStyles.marginTop) || 0;

    if (isMobileMenu) {
      const inputBottom = this.input.getBoundingClientRect().bottom;
      this.resultsMaxHeight = Math.max(180, window.innerHeight - inputBottom - 48);
      return this.resultsMaxHeight;
    }

    const overlayContent = this.overlayContent || this.closest('.search-modal__content--debute');

    if (overlayContent) {
      const overlayStyles = window.getComputedStyle(overlayContent);
      const overlayVerticalPadding =
        (parseFloat(overlayStyles.paddingTop) || 0) + (parseFloat(overlayStyles.paddingBottom) || 0);
      const fieldHeight = field?.getBoundingClientRect().height || 0;
      const availableHeight = overlayContent.clientHeight - overlayVerticalPadding - fieldHeight - resultsTopSpace;

      this.resultsMaxHeight = Math.max(180, availableHeight);
      return this.resultsMaxHeight;
    }

    this.resultsMaxHeight = Math.max(220, window.innerHeight - 160 - resultsTopSpace);
    return this.resultsMaxHeight;
  }

  syncResultsMaxHeight() {
    if (!this.hasAttribute('open') && !this.hasAttribute('loading')) return;

    this.resultsMaxHeight = false;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    requestAnimationFrame(() => this.updateScrollMaskState());
  }

  bindOverlayHeightTransition() {
    const overlay = this.overlayContent;
    if (!overlay) return;

    if (this.overlayHeightTransitionHandler) {
      overlay.removeEventListener('transitionend', this.overlayHeightTransitionHandler);
    }

    this.overlayHeightTransitionHandler = (event) => {
      if (event.target !== overlay || event.propertyName !== 'height') return;

      overlay.removeEventListener('transitionend', this.overlayHeightTransitionHandler);
      this.overlayHeightTransitionHandler = null;
      this.syncResultsMaxHeight();
    };

    overlay.addEventListener('transitionend', this.overlayHeightTransitionHandler);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.syncResultsMaxHeight();
    requestAnimationFrame(() => this.syncResultsMaxHeight());
    this.bindOverlayHeightTransition();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.predictiveSearchResults.classList.remove(
      'predictive-search--can-scroll',
      'predictive-search--at-top',
      'predictive-search--at-bottom'
    );

    if (this.overlayHeightTransitionHandler && this.overlayContent) {
      this.overlayContent.removeEventListener('transitionend', this.overlayHeightTransitionHandler);
      this.overlayHeightTransitionHandler = null;
    }
  }

  updateScrollMaskState() {
    const results = this.predictiveSearchResults;
    const canScroll = results.scrollHeight - results.clientHeight > 1;
    const atTop = results.scrollTop <= 1;
    const atBottom = !canScroll || results.scrollTop + results.clientHeight >= results.scrollHeight - 1;

    results.classList.toggle('predictive-search--can-scroll', canScroll);
    results.classList.toggle('predictive-search--at-top', atTop);
    results.classList.toggle('predictive-search--at-bottom', atBottom);
  }
}

customElements.define('predictive-search', PredictiveSearch);
